import { SeasonLabel, SurchargeGrids, Weekday, WeekdayAmounts } from './types';

const WEEKDAY_BY_UTC_DAY: ReadonlyArray<Weekday> = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Zero padding is not cosmetic here: the month key and the event range are derived by slicing and
 * comparing the string, so `2026-6-1` would look up `2026-6-` and sort after `2026-06-30`.
 */
function isCanonicalIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Parses at UTC midnight so a browser timezone can never move a surcharge onto an adjacent day. */
export function parseIsoDateUtc(isoDate: string): Date | null {
  if (!isCanonicalIsoDate(isoDate)) {
    return null;
  }
  const [year, month, day] = isoDate.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC silently rolls out-of-range parts over (month 13 becomes January), so require a full
  // round-trip rather than pricing a date the CM never typed.
  const roundTrips =
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
  return roundTrips ? parsed : null;
}

export function weekdayOf(isoDate: string): Weekday | null {
  const parsed = parseIsoDateUtc(isoDate);
  return parsed == null ? null : WEEKDAY_BY_UTC_DAY[parsed.getUTCDay()];
}

function seasonalAmountsFor(season: SeasonLabel | undefined, grids: SurchargeGrids): WeekdayAmounts | null {
  if (season === 'high') {
    return grids.high;
  }
  if (season === 'shoulder') {
    return grids.shoulder;
  }
  return null;
}

function periodTotalFor(isoDate: string, weekday: Weekday, grids: SurchargeGrids): number {
  let total = 0;
  for (const period of grids.periods) {
    if (!isCanonicalIsoDate(period.startDate) || !isCanonicalIsoDate(period.endDate)) {
      continue;
    }
    if (isoDate < period.startDate || isoDate > period.endDate) {
      continue;
    }
    // An empty list is "every day", so a period is never silently narrowed to nothing.
    if (period.weekdays.length > 0 && !period.weekdays.includes(weekday)) {
      continue;
    }
    total += period.sellAud;
  }
  return total;
}

export function computeNightlySurcharge(isoDate: string, grids: SurchargeGrids): number {
  const weekday = weekdayOf(isoDate);
  if (weekday == null) {
    return 0;
  }

  const seasonal = seasonalAmountsFor(grids.seasonByMonth[isoDate.slice(0, 7)], grids);
  const nightly = grids.allYearRound[weekday] + (seasonal?.[weekday] ?? 0) + periodTotalFor(isoDate, weekday, grids);

  return Number.isFinite(nightly) ? nightly : 0;
}
